import testUsersDataJson from './../../../../assets/data/testUsers.json';

import { Injectable } from '@angular/core';
import { BillingInfoComponent } from '../../reservation/billing-info/billing-info.component';
import { CreditCardInfoComponent } from '../../reservation/credit-card-info/credit-card-info.component';
import { DriverInfoComponent } from '../../reservation/driver-info/driver-info.component';
import { InsuranceInfoComponent } from '../../reservation/insurance-info/insurance-info.component';
import { Reservation } from './reservation';

export interface TestDriver {
  firstName: string;
  lastName: string;
  email: string;
  isOver25YearsOld: boolean;
  countryCode: string;
  state: string;
  phoneNumber: string;
}

export interface TestCreditCard {
  cardType: string;
  cardNumber: string;
  isOver25YearsOld: boolean;
  expirationDate: {
    year: string;
    month: string;
  }
  cvv: string;
}

export interface TestBilling {
  addressLine1: string;
  addressLine2: string;
  countryCode: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface TestUsersDataJson {
  drivers: Array<TestDriver>;
  creditCards: Array<TestCreditCard>;
  billings: Array<TestBilling>;
}

@Injectable({
  providedIn: 'root'
})
export class DebugPopulateService {

  private reservation: Reservation;
  private testUsersData: TestUsersDataJson;
  private testIndex: number;
  private testCreditCard: TestCreditCard;
  private testDriver: TestDriver;
  private testBilling: TestBilling;
  private creditCardInfoControl: CreditCardInfoComponent;
  private driverInfoControl: DriverInfoComponent;
  private billingInfoControl: BillingInfoComponent;
  private insuranceInfoControl: InsuranceInfoComponent;

  constructor() {
    this.testUsersData = <TestUsersDataJson>(<Object>testUsersDataJson);
    this.testIndex = -1;
  }

  public init(reservation: Reservation,
    creditCardInfoControl: CreditCardInfoComponent,
    driverInfoControl: DriverInfoComponent,
    billingInfoControl: BillingInfoComponent,
    insuranceInfoControl: InsuranceInfoComponent) {
    this.reservation = reservation;
    this.creditCardInfoControl = creditCardInfoControl;
    this.driverInfoControl = driverInfoControl;
    this.billingInfoControl = billingInfoControl;
    this.insuranceInfoControl = insuranceInfoControl;
  }

  public populate() {
    this.testIndex++;
    if (this.testIndex > this.testUsersData.drivers.length - 1) {
      this.testIndex = 0;
    }

    this.testDriver = this.testUsersData.drivers[this.testIndex];
    this.testCreditCard = this.testUsersData.creditCards[this.testIndex];
    this.testBilling = this.testUsersData.billings[0];

    this.prepopulateCreditCardInfo();
    this.prepopulateDriverInfo();
    this.prepopulateBillingInfo();
    this.prepopulateTerms();
    this.prepopulateInsurance();
  }

  private prepopulateCreditCardInfo(): void {
    if (!this.creditCardInfoControl)
      return;

    this.creditCardInfoControl.onCardTypeSelect(this.testCreditCard.cardType);

    if (!this.creditCardInfoControl.creditCardInfo)
      return;

    this.creditCardInfoControl.creditCardInfo.cardholderFirstName = this.testDriver.firstName;
    this.creditCardInfoControl.creditCardInfo.cardholderLastName = this.testDriver.lastName;
    this.creditCardInfoControl.creditCardInfo.cardType = this.testCreditCard.cardType;
    this.creditCardInfoControl.creditCardInfo.cardNumber = this.generateFakeCardNumber(this.testCreditCard.cardNumber);
    this.creditCardInfoControl.creditCardInfo.expirationDate.month = this.testCreditCard.expirationDate.month;
    this.creditCardInfoControl.creditCardInfo.expirationDate.year = this.testCreditCard.expirationDate.year;
    this.creditCardInfoControl.creditCardInfo.cvv = this.testCreditCard.cvv;
  }

  private prepopulateDriverInfo(): void {
    if (!this.driverInfoControl)
      return;

    this.driverInfoControl.onStateSelect();

    if (!this.driverInfoControl.driverInfo)
      return;

    this.driverInfoControl.driverInfo.firstName = this.testDriver.firstName;
    this.driverInfoControl.driverInfo.lastName = this.testDriver.lastName;
    this.driverInfoControl.driverInfo.email = this.testDriver.email;
    this.driverInfoControl.driverInfo.isOver25YearsOld = this.testDriver.isOver25YearsOld;
    this.driverInfoControl.driverInfo.countryCode = this.testDriver.countryCode;
    this.driverInfoControl.driverInfo.stateCode = this.testDriver.state;
    this.driverInfoControl.onCountrySelect(this.driverInfoControl.driverInfo.countryCode);
    
    this.driverInfoControl.driverInfo.phoneNumber = this.testDriver.phoneNumber;
  }

  private prepopulateBillingInfo(): void {
    if (!this.billingInfoControl)
      return;

    this.billingInfoControl.onCountrySelect({ value: this.testBilling.countryCode });

    if (!this.billingInfoControl.billingInfo)
      return;

    this.billingInfoControl.billingInfo.addressLine1 = this.testBilling.addressLine1;
    this.billingInfoControl.billingInfo.addressLine2 = this.testBilling.addressLine2;
    this.billingInfoControl.billingInfo.countryCode = this.testBilling.countryCode;
    this.billingInfoControl.billingInfo.city = this.testBilling.city;
    this.billingInfoControl.billingInfo.zipCode = this.testBilling.zipCode;
    this.billingInfoControl.billingInfo.state = this.testBilling.state;
  }

  private prepopulateTerms(): void {
    if (!this.reservation?.areTermsAndConditionsAccepted)
      return;

    this.reservation.areTermsAndConditionsAccepted = true;
  }

  private prepopulateInsurance(): void {
    if (!this.insuranceInfoControl?.isNotSelected)
      return;

    this.insuranceInfoControl.isNotSelected = true;
  }

  private generateFakeCardNumber(cardNumberIn: string): string {
    if (cardNumberIn === "") {
      return "4111" + "1111" + "1111" + "1111"; //To fool the PCI checker
    }
    return cardNumberIn;
  }
}
